import { useState } from 'react'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import { FormItem, Form } from '@/components/ui/Form'
import Checkbox from '@/components/ui/Checkbox'
import PasswordInput from '@/components/shared/PasswordInput'
import { useAuth } from '@/auth'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import type { ZodType } from 'zod'
import type { CommonProps } from '@/@types/common'
import CaptchaField from '@/components/form/fields/CaptchaField'

interface SignUpFormProps extends CommonProps {
    disableSubmit?: boolean
    setMessage?: (message: string) => void
}

type SignUpFormSchema = {
    name: string
    email: string
    password: string
    password_confirmation: string
    terms_accepted: boolean
    recaptcha?: string | null
}

const validationSchema: ZodType<SignUpFormSchema> = z
    .object({
        name: z
            .string({ required_error: 'Please enter your name' })
            .min(1, { message: 'Please enter your name' }),
        email: z
            .string({ required_error: 'Please enter your email' })
            .min(1, { message: 'Please enter your email' })
            .email({ message: 'Please enter a valid email address' }),
        password: z
            .string({ required_error: 'Please enter a password' })
            .min(8, { message: 'Password must be at least 8 characters' }),
        password_confirmation: z.string({
            required_error: 'Please confirm your password',
        }),
        terms_accepted: z.literal(true, {
            errorMap: () => ({ message: 'You must accept the terms and conditions' }),
        }),
        recaptcha: z.string().optional().nullable(),
    })
    .refine((data) => data.password === data.password_confirmation, {
        message: 'Passwords do not match',
        path: ['password_confirmation'],
    })

const SignUpForm = (props: SignUpFormProps) => {
    const { disableSubmit = false, className, setMessage } = props

    const [isSubmitting, setSubmitting] = useState<boolean>(false)
    const [captchaKey, setCaptchaKey] = useState(0)

    const { signUp } = useAuth()

    const {
        handleSubmit,
        formState: { errors },
        control,
        resetField,
    } = useForm<SignUpFormSchema>({
        resolver: zodResolver(validationSchema),
    })

    const resetCaptcha = () => {
        resetField('recaptcha')
        setCaptchaKey((key) => key + 1)
    }

    const onSignUp = async (values: SignUpFormSchema) => {
        if (disableSubmit) return

        setSubmitting(true)
        const result = await signUp({
            name: values.name,
            email: values.email,
            password: values.password,
            password_confirmation: values.password_confirmation,
            terms_accepted: values.terms_accepted,
            recaptcha: values.recaptcha,
        })

        if (result?.status === 'failed') {
            setMessage?.(result.message)
            resetCaptcha()
        }

        setSubmitting(false)
    }

    return (
        <div className={className}>
            <Form onSubmit={handleSubmit(onSignUp, resetCaptcha)}>
                <FormItem
                    label="Name"
                    invalid={Boolean(errors.name)}
                    errorMessage={errors.name?.message}
                >
                    <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                            <Input
                                type="text"
                                placeholder="Your name"
                                autoComplete="name"
                                {...field}
                            />
                        )}
                    />
                </FormItem>
                <FormItem
                    label="Email"
                    invalid={Boolean(errors.email)}
                    errorMessage={errors.email?.message}
                >
                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <Input
                                type="email"
                                placeholder="Email"
                                autoComplete="email"
                                {...field}
                            />
                        )}
                    />
                </FormItem>
                <FormItem
                    label="Password"
                    invalid={Boolean(errors.password)}
                    errorMessage={errors.password?.message}
                >
                    <Controller
                        name="password"
                        control={control}
                        render={({ field }) => (
                            <PasswordInput
                                placeholder="Password"
                                autoComplete="new-password"
                                {...field}
                            />
                        )}
                    />
                </FormItem>
                <FormItem
                    label="Confirm Password"
                    invalid={Boolean(errors.password_confirmation)}
                    errorMessage={errors.password_confirmation?.message}
                >
                    <Controller
                        name="password_confirmation"
                        control={control}
                        render={({ field }) => (
                            <PasswordInput
                                placeholder="Confirm Password"
                                autoComplete="new-password"
                                {...field}
                            />
                        )}
                    />
                </FormItem>

                <CaptchaField
                    key={captchaKey}
                    name="recaptcha"
                    control={control}
                    className="my-4"
                />

                <FormItem
                    invalid={Boolean(errors.terms_accepted)}
                    errorMessage={errors.terms_accepted?.message}
                    className="mb-4"
                >
                    <Controller
                        name="terms_accepted"
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                            <Checkbox
                                checked={field.value}
                                onChange={field.onChange}
                            >
                                I agree to the{' '}
                                <a
                                    href="/terms"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline hover:text-primary"
                                >
                                    Terms of Service
                                </a>{' '}
                                and{' '}
                                <a
                                    href="/privacy"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline hover:text-primary"
                                >
                                    Privacy Policy
                                </a>
                            </Checkbox>
                        )}
                    />
                </FormItem>

                <Button
                    block
                    loading={isSubmitting}
                    variant="solid"
                    type="submit"
                >
                    {isSubmitting ? 'Creating Account...' : 'Create Account'}
                </Button>
            </Form>
        </div>
    )
}

export default SignUpForm
